import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#filterSlides();
    this.play();
  }

  #filterSlides() {
    const templateName = this.dataset.templateName || '';
    const templateSuffix = this.dataset.templateSuffix || '';
    const currentTemplate = templateSuffix ? `${templateName}.${templateSuffix}` : templateName;

    const slides = this.querySelectorAll('.announcement-bar__slide');
    let hasSpecificMatch = false;

    slides.forEach((slide) => {
      const showOn = slide.dataset.showOnTemplate || 'all';
      slide.dataset.matched = showOn === currentTemplate ? 'true' : 'false';
      if (showOn !== 'all' && showOn === currentTemplate) {
        hasSpecificMatch = true;
      }
    });

    const visibleSlides = [];
    slides.forEach((slide) => {
      const showOn = slide.dataset.showOnTemplate || 'all';
      const matched = slide.dataset.matched === 'true';
      const shouldShow = matched || (showOn === 'all' && !hasSpecificMatch);
      slide.dataset.visible = shouldShow ? 'true' : 'false';
      slide.hidden = !shouldShow;
      if (shouldShow) visibleSlides.push(slide);
    });

    const arrows = this.querySelector('slideshow-arrows');
    if (arrows) {
      arrows.style.display = visibleSlides.length > 1 ? '' : 'none';
    }

    slides.forEach((slide) => {
      const index = visibleSlides.indexOf(slide);
      slide.setAttribute('aria-hidden', `${index !== 0}`);
    });
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    const visibleSlides = (this.refs.slides ?? []).filter(
      (slide) => slide.dataset.visible === 'true'
    );

    if (visibleSlides.length === 0) return;

    let relativeIndex = current % visibleSlides.length;
    if (relativeIndex < 0) {
      relativeIndex += visibleSlides.length;
    }

    (this.refs.slides ?? []).forEach((slide) => {
      const slideIndex = visibleSlides.indexOf(slide);
      slide.setAttribute('aria-hidden', `${slideIndex !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
